import { type CSSProperties, useState } from 'react';
import type {
  UseFormRegister,
  UseFormReturn,
  UseFormWatch,
} from 'react-hook-form';

import {
  hasNumberOrSpecialCharacter,
  hasUppercaseLetter,
  isValidPassword,
} from '@revelio/auth';

import type {
  FormValuesPassRequired,
  PasswordResetFormValuesRequired,
} from './models';
import { useThemeMode } from './theme/theme';
import { Field, IconButton, TextInput, uiStyles } from './ui';
import { AnchoredPopover } from './ui/anchored-popover';
import { CheckCircleIcon, ViewIcon, ViewOffIcon } from './ui/icons';

function Requirement({ met, children }: { met: boolean; children: string }) {
  return (
    <li>
      <CheckCircleIcon
        className={`${uiStyles.popoverIcon}${
          met ? ` ${uiStyles.popoverIconMet}` : ''
        }`}
      />
      {children}
    </li>
  );
}

export const PasswordInput = ({
  register,
  watch,
  errors,
}: {
  register: UseFormRegister<
    FormValuesPassRequired | PasswordResetFormValuesRequired
  >;
  watch: UseFormWatch<FormValuesPassRequired | PasswordResetFormValuesRequired>;
  errors: UseFormReturn<
    FormValuesPassRequired | PasswordResetFormValuesRequired
  >['formState']['errors'];
}) => {
  // Inside a ThemeLayout we serve the themed field; elsewhere (settings)
  // there's no auth context, so fall back to the un-themed field.
  const themed = !!useThemeMode();
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);

  const [showPopover, setShowPopover] = useState(false);
  const handleFocus = () => setShowPopover(true);
  const handleBlur = () => setShowPopover(false);

  const [showPassword, setShowPassword] = useState(false);
  const handleToggleShowPassword = () => setShowPassword((prevVal) => !prevVal);

  const watchPassword = watch('password', '');

  return (
    <div ref={setAnchor}>
      <Field error={errors['password']?.message}>
        <TextInput
          onFocus={handleFocus}
          placeholder="Password"
          aria-label="Password"
          // Every PasswordInput site SETS a password (signup, settings,
          // reset) — new-password also cues managers to offer generation.
          autoComplete="new-password"
          type={showPassword ? 'text' : 'password'}
          invalid={!!errors['password']}
          className={themed ? undefined : uiStyles.inputPlain}
          rightSlotStyle={{ '--ap-right-slot-mr': '10px' } as CSSProperties}
          rightSlot={
            <IconButton
              className={themed ? undefined : uiStyles.iconButtonFaded}
              aria-label="Toggle view password"
              onClick={handleToggleShowPassword}
            >
              {showPassword ? <ViewOffIcon /> : <ViewIcon />}
            </IconButton>
          }
          {...register('password', {
            required: 'Password is required',
            validate: {
              passwordSyntax: (password: string) =>
                isValidPassword(password) ? true : 'Password is invalid',
            },
            onBlur: handleBlur,
          })}
        />
      </Field>
      <AnchoredPopover anchor={anchor} open={showPopover}>
        <ul className={uiStyles.popoverList}>
          <Requirement met={watchPassword?.length > 7}>
            At least 8 characters
          </Requirement>
          <Requirement met={hasUppercaseLetter(watchPassword || '')}>
            At least 1 uppercase letter
          </Requirement>
          <Requirement met={hasNumberOrSpecialCharacter(watchPassword || '')}>
            At least 1 number or special character
          </Requirement>
        </ul>
      </AnchoredPopover>
    </div>
  );
};
